import React, { useEffect, useMemo } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ProgrammeModel, ProgrammePaymentAccountJSON } from '@/models/programme.model'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/Select'
import { Checkbox } from '@/components/ui/Checkbox'
import { Button } from '@/components/ui/button'
import { CheckCircleIcon } from 'lucide-react'

const segmentSchema = z.object({
  name: z.string().min(1, 'Segment name is required'),
  length: z
    .string()
    .min(1, 'Segment length is required')
    .refine((value) => Number(value) > 0, 'Segment length must be greater than zero'),
  unit: z.string().min(1, 'Segment unit is required'),
  order: z
    .string()
    .min(1, 'Segment order is required')
    .refine((value) => Number(value) >= 0, 'Segment order must be zero or greater'),
  description: z.string().optional(),
})

const programmeFormSchema = z.object({
  title: z.string().min(1, 'Programme title is required'),
  description: z.string().min(1, 'Description is required'),
  duration: z
    .string()
    .min(1, 'Duration is required')
    .refine((value) => Number(value) > 0, 'Duration must be greater than zero'),
  durationUnit: z.string().min(1, 'Duration unit is required'),
  type: z.string().min(1, 'Programme type is required'),
  isActive: z.boolean(),
  departmentId: z.string().min(1, 'Department is required'),
  segments: z.array(segmentSchema).min(1, 'Add at least one segment'),
  admissionRequirements: z.object({
    requiresJamb: z.boolean(),
    requiresOLevel: z.boolean(),
    requiredDocuments: z.array(z.string().min(1)).optional(),
  }),
})

export type ProgrammeFormValues = z.infer<typeof programmeFormSchema>

const defaultSegment: ProgrammeFormValues['segments'][number] = {
  name: '',
  length: '',
  unit: 'months',
  order: '1',
  description: '',
}

const defaultAdmissionRequirements: ProgrammeFormValues['admissionRequirements'] = {
  requiresJamb: false,
  requiresOLevel: false,
  requiredDocuments: [
    'meansOfIdentification',
    'certificateFile',
    'resultFile',
    'paymentReceipt',
  ],
}

const ADMISSION_DOCUMENT_OPTIONS = [
  { value: 'meansOfIdentification', label: 'Means of Identification' },
  { value: 'certificateFile', label: 'Certificate File' },
  { value: 'resultFile', label: 'Result File' },
  { value: 'paymentReceipt', label: 'Payment Receipt' },
]

const programmeTypeOptions = [
  { value: 'diploma', label: 'Diploma' },
  { value: 'short_course', label: 'Short Course' },
]

interface ProgrammeFormProps {
  mode: 'create' | 'edit'
  programme?: ProgrammeModel | null
  departments: Array<{ id: string; name: string }>
  submitting?: boolean
  onSubmit: (values: ProgrammeFormValues) => Promise<void> | void
  onCancel?: () => void
  submitLabel?: string
}

export const ProgrammeForm: React.FC<ProgrammeFormProps> = ({
  mode,
  programme,
  departments,
  submitting = false,
  onSubmit,
  onCancel,
  submitLabel,
}) => {
  const departmentOptions = useMemo(() => {
    if (departments.length > 0) {
      return departments
    }

    if (programme?.department_id) {
      return [
        {
          id: programme.department_id,
          name: 'Current department',
        },
      ]
    }

    return []
  }, [departments, programme?.department_id])

  const defaultValues: ProgrammeFormValues = useMemo(() => {
    const existingSegments =
      programme?.segments?.length && programme.segments.length > 0
        ? programme.segments.map((segment, index) => ({
          name: segment.name ?? `Segment ${index + 1}`,
          length: segment.length !== undefined ? String(segment.length) : '',
          unit: segment.unit ?? '',
          order: segment.order !== undefined ? String(segment.order) : String(index + 1),
          description: segment.description ?? '',
        }))
        : [defaultSegment]

    const existingRequirements = programme?.admission_requirements
      ? {
        requiresJamb: programme.admission_requirements.requiresJamb ?? false,
        requiresOLevel: programme.admission_requirements.requiresOLevel ?? false,
        requiredDocuments:
          programme.admission_requirements.requiredDocuments?.length
            ? programme.admission_requirements.requiredDocuments
            : defaultAdmissionRequirements.requiredDocuments,
      }
      : defaultAdmissionRequirements

    return {
      title: programme?.title ?? '',
      description: programme?.description ?? '',
      duration: programme?.duration ? String(programme.duration) : '',
      durationUnit: programme?.duration_unit ?? 'months',
      type: programme?.type ?? 'diploma',
      isActive: programme?.is_active ?? true,
      departmentId: programme?.department_id ?? (departmentOptions[0]?.id ?? ''),
      segments: existingSegments,
      admissionRequirements: existingRequirements,
    }
  }, [programme, departmentOptions])

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProgrammeFormValues>({
    resolver: zodResolver(programmeFormSchema),
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const {
    fields: segmentFields,
    append: appendSegment,
    remove: removeSegment,
  } = useFieldArray<ProgrammeFormValues>({
    control,
    name: 'segments',
  })

  const segments = watch('segments') ?? []

  const handleAddSegment = () => {
    appendSegment({
      ...defaultSegment,
      order: String(segments.length + 1),
    })
  }

  const handleRemoveSegment = (index: number) => {
    removeSegment(index)
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} noValidate>
      <section className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Programme Title *
          </label>
          <Input
            placeholder="Diploma in Business Management"
            {...register('title')}
            className={errors.title ? 'border-red-500' : ''}
          />
          {errors.title && (
            <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Description *
          </label>
          <textarea
            rows={4}
            className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40 ${errors.description ? 'border-red-500' : 'border-secondary-300'
              }`}
            placeholder="Describe the outcomes and key focus for this programme."
            {...register('description')}
          />
          {errors.description && (
            <p className="text-xs text-red-500 mt-1">{errors.description.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Duration *
          </label>
          <Input
            type="number"
            min={1}
            placeholder="12"
            {...register('duration')}
            className={errors.duration ? 'border-red-500' : ''}
          />
          {errors.duration && (
            <p className="text-xs text-red-500 mt-1">{errors.duration.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Duration Unit *
          </label>
          <select
            className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40 ${errors.durationUnit ? 'border-red-500' : 'border-secondary-300'
              }`}
            {...register('durationUnit')}
          >
            <option value="" disabled>
              Select unit
            </option>
            <option value="days">Days</option>
            <option value="weeks">Weeks</option>
            <option value="months">Months</option>
            <option value="years">Years</option>
          </select>
          {errors.durationUnit && (
            <p className="text-xs text-red-500 mt-1">{errors.durationUnit.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Department *
          </label>
          <select
            className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40 ${errors.departmentId ? 'border-red-500' : 'border-secondary-300'
              }`}
            {...register('departmentId')}
          >
            <option value="" disabled>
              Select department
            </option>
            {departmentOptions.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
          {errors.departmentId && (
            <p className="text-xs text-red-500 mt-1">{errors.departmentId.message}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            {...register('isActive')}
            label="Programme is active"
          />
        </div>
      </section>

      <section className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Programme Type *
            </label>
            <select
              className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40 ${errors.type ? 'border-red-500' : 'border-secondary-300'
                }`}
              {...register('type')}
            >
              <option value="" disabled>
                Select programme type
              </option>
              {programmeTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.type && (
              <p className="text-xs text-red-500 mt-1">{errors.type.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-secondary-900">Segments</h3>
            <Button type="button" variant="outline" size="sm" onClick={handleAddSegment}>
              Add Segment
            </Button>
          </div>

          {segmentFields.map((field, index) => (
            <div key={field.id} className="space-y-4 rounded-lg border border-secondary-200 p-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-secondary-900">
                  Segment {index + 1}
                </h4>
                {segmentFields.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveSegment(index)}>
                    Remove
                  </Button>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Segment Name *
                  </label>
                  <Input
                    placeholder="Intensive Sessions"
                    {...register(`segments.${index}.name` as const)}
                    className={errors.segments?.[index]?.name ? 'border-red-500' : ''}
                  />
                  {errors.segments?.[index]?.name && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.segments[index]?.name?.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Length *
                    </label>
                    <Select
                      {...register(`segments.${index}.length` as const)}
                      className={errors.segments?.[index]?.length ? 'border-red-500' : ''}
                    >
                      <option value="">Select length</option>
                      {Array.from({ length: 24 }, (_, i) => i + 1).map((val) => (
                        <option key={val} value={val}>
                          {val}
                        </option>
                      ))}
                    </Select>
                    {errors.segments?.[index]?.length && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.segments[index]?.length?.message}
                      </p>
                    )}
                  </div>
                  <div className="mt-7">
                    <Input
                      placeholder="days"
                      {...register(`segments.${index}.unit` as const)}
                      className={errors.segments?.[index]?.unit ? 'border-red-500' : ''}
                    />
                  </div>
                  {/* <div className="mt-7">
                    <Input
                      placeholder="Order"
                      type="number"
                      min={0}
                      {...register(`segments.${index}.order` as const)}
                      className={errors.segments?.[index]?.order ? 'border-red-500' : ''}
                    />
                  </div> */}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    className="w-full rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40"
                    placeholder="Describe this segment."
                    {...register(`segments.${index}.description` as const)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <h3 className="text-base font-semibold text-secondary-900">Admission Requirements</h3>
          <div className="rounded-lg border border-secondary-200 p-4 space-y-4">
            <div className="flex flex-wrap gap-4">
              <Checkbox
                {...register('admissionRequirements.requiresJamb')}
                label="JAMB required"
              />
              <Checkbox
                {...register('admissionRequirements.requiresOLevel')}
                label="O-Level required"
              />
            </div>
            <div className="space-y-3">
              <span className="text-sm font-medium text-secondary-700">
                Required Documents
              </span>
              <Controller
                control={control}
                name="admissionRequirements.requiredDocuments"
                render={({ field: { value = [], onChange } }) => {
                  const handleToggleDocument = (documentValue: string) => {
                    const current = Array.isArray(value) ? value : []
                    const next = current.includes(documentValue)
                      ? current.filter((item) => item !== documentValue)
                      : [...current, documentValue]

                    onChange(next)
                    setValue('admissionRequirements.requiredDocuments', next, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }

                  return (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {ADMISSION_DOCUMENT_OPTIONS.map((option) => {
                          const isSelected = value?.includes(option.value)
                          return (
                            <button
                              type="button"
                              key={option.value}
                              onClick={() => handleToggleDocument(option.value)}
                              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${isSelected
                                ? 'border-[#7b2a40] bg-[#7b2a40] text-white shadow-sm'
                                : 'border-secondary-200 bg-secondary-50 text-secondary-700 hover:bg-secondary-100'
                                }`}
                            >
                              <span>{option.label}</span>
                              {isSelected && <CheckCircleIcon className="h-4 w-4" />}
                            </button>
                          )
                        })}
                      </div>
                      {value?.length === 0 && (
                        <p className="text-sm text-secondary-500">
                          Select the documents applicants must upload.
                        </p>
                      )}
                      {errors.admissionRequirements?.requiredDocuments && (
                        <p className="text-xs text-red-500">
                          {errors.admissionRequirements.requiredDocuments?.message as string}
                        </p>
                      )}
                    </div>
                  )
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          {submitting
            ? 'Saving...'
            : submitLabel ?? (mode === 'create' ? 'Create Programme' : 'Save Changes')}
        </Button>
      </div>
    </form>
  )
}

export interface ProgrammeAdmissionRequirementsMutationPayload {
  requires_jamb?: boolean | null
  requires_o_level?: boolean | null
  required_documents?: string[] | null
}

export interface ProgrammeSegmentMutationPayload {
  name?: string | null
  length?: number | null
  unit?: string | null
  order?: number | null
  description?: string | null
}

export interface ProgrammeMutationPayloadInput {
  title: string
  description: string
  duration: number
  duration_unit: string
  type: string
  is_active: boolean
  departmentId?: string | null
  segments: ProgrammeSegmentMutationPayload[]
  admission_requirements?: ProgrammeAdmissionRequirementsMutationPayload
}

export function mapProgrammeFormValuesToPayload(
  values: ProgrammeFormValues,
): ProgrammeMutationPayloadInput {
  const parsedDuration = Number(values.duration)

  const segments = values.segments.map((segment) => ({
    name: segment.name,
    length: segment.length ? Number(segment.length) : undefined,
    unit: segment.unit,
    order: segment.order ? Number(segment.order) : undefined,
    description: segment.description || undefined,
  }))

  const admissionRequirements: ProgrammeAdmissionRequirementsMutationPayload = {
    requires_jamb: values.admissionRequirements.requiresJamb,
    requires_o_level: values.admissionRequirements.requiresOLevel,
    required_documents: values.admissionRequirements.requiredDocuments
      ?.map((doc) => doc.trim())
      .filter((doc) => doc.length > 0),
  }

  if (!admissionRequirements.required_documents?.length) {
    admissionRequirements.required_documents = undefined
  }

  return {
    title: values.title.trim(),
    description: values.description.trim(),
    duration: Number.isNaN(parsedDuration) ? 0 : parsedDuration,
    duration_unit: values.durationUnit,
    type: values.type,
    is_active: values.isActive,
    departmentId: values.departmentId || undefined,
    segments,
    admission_requirements: admissionRequirements,
  }
}
